import { Loader2, Truck } from "lucide-react";
import { useNotify } from "ra-core";
import { useEffect, useState } from "react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

import { AddressAlertBox } from "./AddressAlertBox";
import { PRODUCTS, formatEuro, labelOf } from "./gulaConstants";
import { fullName, type GulaOrder } from "./gulaTypes";
import {
  createPacklinkShipment,
  quotePacklink,
  type PacklinkOption,
} from "./packlink";

const formatDay = (date: string | null) => {
  if (!date) return "";
  const [year, month, day] = date.split("/").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("es-ES", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

/** Compares Packlink prices for an order and creates the shipment. */
export const PacklinkDialog = ({
  order,
  onClose,
  onCreated,
  onAddressResolved,
}: {
  order: GulaOrder;
  onClose: () => void;
  onCreated: () => void;
  onAddressResolved: () => void;
}) => {
  const notify = useNotify();
  const [options, setOptions] = useState<PacklinkOption[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let active = true;
    setOptions(null);
    setError(null);
    quotePacklink(order.id)
      .then(({ options }) => {
        if (!active) return;
        setOptions(options);
        setSelected(options[0]?.id ?? null);
      })
      .catch((err: Error) => active && setError(err.message));
    return () => {
      active = false;
    };
  }, [order.id, order.postal_code]);

  // First (cheapest) of the options that arrive soonest
  const fastestId = options?.length
    ? [...options].sort((x, y) =>
        (x.deliveryDate ?? "9999").localeCompare(y.deliveryDate ?? "9999"),
      )[0].id
    : null;

  const handleCreate = async () => {
    if (selected == null) return;
    setCreating(true);
    try {
      const { reference } = await createPacklinkShipment(order.id, selected);
      notify(
        `Envío ${reference} creado en Packlink. Falta pagarlo allí para que salga.`,
        { type: "success" },
      );
      onCreated();
    } catch (err) {
      notify(err instanceof Error ? err.message : "No se ha podido crear", {
        type: "error",
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Enviar con Packlink</DialogTitle>
          <DialogDescription>
            {labelOf(PRODUCTS, order.product)}. Precios para entrega a
            domicilio, con el paquete guardado en Packlink.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border bg-muted/40 p-3 text-sm">
          <div className="mb-1 flex items-center justify-between gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Se enviará a
            </span>
            <Link
              to={`/clientes?cliente=${order.client_id}`}
              className="text-xs text-muted-foreground hover:underline"
              onClick={onClose}
            >
              Corregir en la ficha
            </Link>
          </div>
          <div className="font-medium">{fullName(order)}</div>
          <div>{order.address || "Sin dirección"}</div>
          <div>
            {[order.postal_code, order.city].filter(Boolean).join(" ")}
            {order.province ? ` (${order.province})` : ""}
          </div>
          <div className="text-muted-foreground">
            {[order.phone, order.email].filter(Boolean).join(" · ")}
          </div>
        </div>

        {order.address_alert ? (
          <AddressAlertBox
            clientId={order.client_id}
            current={order}
            alert={order.address_alert}
            onResolved={onAddressResolved}
          />
        ) : null}

        {error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : !options ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Consultando precios…
          </p>
        ) : !options.length ? (
          <p className="text-sm text-muted-foreground">
            Packlink no ofrece servicios para este código postal.
          </p>
        ) : (
          <div className="flex flex-col gap-2" role="radiogroup">
            {options.map((option, index) => (
              <button
                key={option.id}
                type="button"
                role="radio"
                aria-checked={selected === option.id}
                onClick={() => setSelected(option.id)}
                className={cn(
                  "flex items-center justify-between gap-3 rounded-lg border p-3 text-left text-sm transition-colors",
                  selected === option.id
                    ? "border-primary bg-primary/5"
                    : "hover:bg-muted/50",
                )}
              >
                <div className="flex min-w-0 flex-col gap-1">
                  <span className="font-medium">
                    {option.carrier} · {option.service}
                  </span>
                  <span className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                    <Badge variant="secondary">
                      {option.pickup
                        ? "Recogen en la nave"
                        : "Lo lleváis a un punto"}
                    </Badge>
                    {option.deliveryDate
                      ? `Entrega aprox. ${formatDay(option.deliveryDate)}`
                      : null}
                    {index === 0 ? <Badge>Más barato</Badge> : null}
                    {option.id === fastestId ? (
                      <Badge variant="outline">Más rápido</Badge>
                    ) : null}
                  </span>
                </div>
                <div className="flex flex-col items-end whitespace-nowrap">
                  <span className="font-semibold tabular-nums">
                    {formatEuro(option.price)}
                  </span>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {formatEuro(option.priceWithVat)} con IVA
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:items-center">
          <p className="text-xs text-muted-foreground sm:mr-auto">
            Se crea en Packlink con la dirección del cliente. El pago se hace en
            Packlink.
          </p>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleCreate}
            disabled={creating || selected == null || !!order.address_alert}
          >
            <Truck className="h-4 w-4" />
            {creating ? "Creando…" : "Crear envío"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
